import type { Component } from 'vue';
import { createApp } from 'vue';
import PrimeVue from 'primevue/config';
import fontAwesome from '@/plugins/fontAwesome';
import luxon from '@/plugins/luxon';
import { Plugin as TrimZvdZOPlugin } from '@/common/text/trim-zvdzo';
import '@fontsource/inter/index.css';
import '@/styles/main.scss';
import matomoPlugin from '@/plugins/matomo';
import i18n from '@/plugins/i18n';
import vuetify from '@/plugins/vuetify';
import { appendStyleSheet } from '@/common/accessible-color-palette';
import ZvdZApp from './app.vue';
import { plugin as routerPlugin } from './plugins/router';
import globalComponents from './global-components';
import { DefaultApolloClient } from '@vue/apollo-composable';
import createDefaultApolloClient from '@/plugins/vueApollo';
import { AuthError, PublicClientApplication } from '@azure/msal-browser';
import { showSnackbar } from '@/common/composition/snackbar';
import faviconUrl from '@/assets/favicon-zeker-arbo-32x32.png'; // Vite will handle cache-busting
import {
  isZekerArbo,
  setNetwerkbedrijfBooleans,
} from './common/netwerkbedrijven';
import { getMsalInstanceShared } from '@/authConfig';

setNetwerkbedrijfBooleans();
if (isZekerArbo.value) {
  const globalI18n = i18n.global; // Gebruik de global API

  // Update i18n global translation for ZekerArbo
  globalI18n.availableLocales.forEach(locale => {
    globalI18n.setLocaleMessage(locale, {
      ...globalI18n.getLocaleMessage(locale),
      // Gebruik globalI18n.t() voor de vertaalde waarden
      appTitle: globalI18n.t('appTitleZekerArbo'),
      netwerkbedrijf: globalI18n.t('netwerkbedrijfZekerArbo'),
      ICT_BV: globalI18n.t('ICT_BV_ZekerArbo'),
      appTitleMijn: globalI18n.t('appTitleMijnZekerArbo'),
      netwerk: globalI18n.t('netwerkZekerArbo'),
      hulprolBeheerZvdZAccount: globalI18n.t(
        'roleName.hulprolBeheerZvdZAccount_ZekerArbo',
      ),
    });
  });

  // Stel de documenttitel en favicon in
  document.title = globalI18n.t('appTitle');
  const faviconLink = document.querySelector(
    "link[rel~='icon']",
  ) as HTMLLinkElement;
  if (faviconLink) {
    faviconLink.href = faviconUrl;
  }
}

async function bootstrap() {
  const msalInstance = getMsalInstanceShared();

  // 1. Initialiseer de MSAL instance, gebaseerd op de configuratie
  await msalInstance.initialize();

  // 2. Vang de redirect af (of het nu SSO of normaal is, deze vangt het af)
  await handleMsalRedirect(msalInstance);

  appendStyleSheet();
  const app = createApp(ZvdZApp as unknown as Component);

  // Create and provide Apollo client globally
  const apolloClient = createDefaultApolloClient();
  app.provide(DefaultApolloClient, apolloClient);

  app.use(luxon);
  app.use(i18n);
  app.use(routerPlugin);
  app.use(vuetify());
  app.use(fontAwesome);
  app.use(globalComponents);
  app.use(TrimZvdZOPlugin);
  app.use(matomoPlugin);
  app.use(PrimeVue);
  app.mount('#app');
}

async function handleMsalRedirect(msalInstance: PublicClientApplication) {
  try {
    const redirectResult = await msalInstance.handleRedirectPromise();

    if (redirectResult) {
      msalInstance.setActiveAccount(redirectResult.account);
    }
  } catch (error: unknown) {
    const t = i18n.global.t;

    if (error instanceof AuthError) {
      if (
        error.errorCode === 'access_denied' ||
        error.message?.includes('AADB2C90091')
      ) {
        showSnackbar(t('login.cancelled'), 'info');
        return;
      }
      showSnackbar(
        t('login.errorWithCode', { code: error.errorCode }),
        'error',
      );
    } else {
      showSnackbar(t('login.unexpectedError'), 'error');
    }
  }
}

bootstrap();
